import Swiper from 'swiper'
import { Scrollbar } from 'swiper/modules'

function setupHeader(root) {
  const header = root.querySelector('#principal-header')

  if (!header) return

  // Use the same initial/scrolled states as the homepage header.
  header.classList.add('isWhite')
}

function setupTimelineProgress(root) {
  const timeline = root.querySelector('.a2-timeline')
  const progress = timeline?.querySelector('[data-a2-timeline-progress]')
  const endpoint = timeline?.querySelector('[data-a2-timeline-endpoint]')

  if (!timeline || !progress || progress.dataset.a2Ready === 'true') return

  progress.dataset.a2Ready = 'true'

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    progress.style.setProperty('--a2-timeline-progress', '1')
    endpoint?.style.setProperty(
      '--a2-timeline-progress-px',
      `${Math.max(0, progress.offsetHeight - 4)}px`,
    )
    return
  }

  let frame = null

  const update = () => {
    frame = null

    const rect = timeline.getBoundingClientRect()
    const start = window.innerHeight * 0.8
    const end = window.innerHeight * 0.2
    const distance = rect.height + start - end
    const value = Math.min(1, Math.max(0, (start - rect.top) / distance))

    progress.style.setProperty('--a2-timeline-progress', value.toFixed(4))
    const endpointPosition = Math.max(
      -4,
      Math.min(progress.offsetHeight - 4, progress.offsetHeight * value - 4),
    )
    endpoint?.style.setProperty('--a2-timeline-progress-px', `${endpointPosition}px`)
  }

  const requestUpdate = () => {
    if (frame !== null) return
    frame = window.requestAnimationFrame(update)
  }

  window.addEventListener('scroll', requestUpdate, { passive: true })
  window.addEventListener('resize', requestUpdate)
  update()
}

function setupQuestionToggles(root) {
  root.querySelectorAll('[data-a2-question]').forEach((question) => {
    const button = question.querySelector('[data-a2-question-toggle]')
    const answer = question.querySelector('[data-a2-question-answer]')

    if (!button || !answer || button.dataset.a2Ready === 'true') return

    button.dataset.a2Ready = 'true'
    answer.hidden = true

    button.addEventListener('click', () => {
      const isOpen = button.getAttribute('aria-expanded') === 'true'
      const nextOpen = !isOpen

      button.setAttribute('aria-expanded', String(nextOpen))
      question.classList.toggle('is-open', nextOpen)
      answer.hidden = !nextOpen

      const label = button.querySelector('span')
      if (label) {
        label.textContent = nextOpen
          ? button.dataset.labelClose
          : button.dataset.labelOpen
      }
    })
  })
}

function setupQuestionsSlider(root) {
  const slider = root.querySelector('[data-a2-questions-slider]')
  const scrollbar = slider?.querySelector('.a2-questions__scrollbar')

  if (!slider || !scrollbar || slider.dataset.a2Ready === 'true') return

  slider.dataset.a2Ready = 'true'

  const mobile = window.matchMedia('(max-width: 767px)')
  let instance = null

  const sync = () => {
    if (mobile.matches && !instance) {
      instance = new Swiper(slider, {
        modules: [Scrollbar],
        slidesPerView: 'auto',
        spaceBetween: 0,
        resistanceRatio: 0.65,
        scrollbar: {
          el: scrollbar,
          draggable: true,
          hide: false,
        },
      })
    }

    if (!mobile.matches && instance) {
      instance.destroy(true, true)
      instance = null
    }
  }

  sync()
  mobile.addEventListener?.('change', sync)
}

function setupVideo(root) {
  const video = root.querySelector('[data-a2-video]')
  const button = root.querySelector('[data-a2-video-play]')

  if (!video || !button || button.dataset.a2Ready === 'true') return

  button.dataset.a2Ready = 'true'

  const hasSource = Boolean(video.currentSrc || video.getAttribute('src') || video.querySelector('source[src]'))
  const playLabel = button.dataset.labelPlay || 'Reproduzir vídeo'
  const pauseLabel = button.dataset.labelPause || 'Pausar vídeo'
  button.disabled = !hasSource
  button.classList.toggle('is-disabled', !hasSource)

  if (!hasSource) return

  const syncState = () => {
    button.classList.toggle('is-playing', !video.paused && !video.ended)
    button.setAttribute('aria-label', video.paused ? playLabel : pauseLabel)
  }

  button.addEventListener('click', () => {
    if (video.paused) video.play()
    else video.pause()
  })
  video.addEventListener('play', syncState)
  video.addEventListener('pause', syncState)
  video.addEventListener('ended', syncState)
}

export default function PageLeiteA2() {
  const root = document.querySelector('[js-page="leite-a2"]')

  if (!root || root.dataset.a2Enhanced === 'true') return

  root.dataset.a2Enhanced = 'true'
  setupHeader(root)
  setupTimelineProgress(root)
  setupQuestionsSlider(root)
  setupQuestionToggles(root)
  setupVideo(root)
}
